"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { MapPin, Phone, Mail, Linkedin, Facebook, Loader, ArrowRight, Clock, Calendar } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const CONTACT_INFO = [{
  icon: MapPin,
  label: "Lounge Location",
  value: "3333 West Division Street Suite 145B, St Cloud MN"
}, {
  icon: Phone,
  label: "Concierge Line",
  value: "320-217-2545"
}, {
  icon: Mail,
  label: "Email Inquiries",
  value: "Info@shifahydrationlounge.com"
}];
const SOCIAL_LINKS = [{
  name: "Instagram",
  icon: Linkedin,
  url: "#",
  handle: "@alshifa_lounge"
}, {
  name: "Facebook",
  icon: Facebook,
  url: "#",
  handle: "Al Shifa Hydration"
}];
const SERVICE_OPTIONS = ["Premium IV Therapy", "HydraFacial Skin Boosters", "Immune & Energy Boost", "Other Wellness Services"];
function StrategyForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-700">
        <div className="size-20 bg-primary/10 text-primary rounded-full flex items-center justify-center mb-4 border border-primary/20 shadow-[0_0_30px_rgba(184,155,95,0.2)]">
          <svg className="size-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="font-serif text-3xl font-bold text-foreground">Inquiry Received</h3>
        <p className="text-muted-foreground max-w-md font-default leading-relaxed">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary/30 text-primary hover:bg-primary/5 rounded-full px-8">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-bold uppercase tracking-widest text-primary/70">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Elias Thorne" className="bg-transparent border-x-0 border-t-0 border-b border-primary/20 focus-visible:border-primary focus-visible:ring-0 rounded-none px-0 h-12 text-lg transition-all duration-300 placeholder:text-muted-foreground/30" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-bold uppercase tracking-widest text-primary/70">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="elias@luxury.com" className="bg-transparent border-x-0 border-t-0 border-b border-primary/20 focus-visible:border-primary focus-visible:ring-0 rounded-none px-0 h-12 text-lg transition-all duration-300 placeholder:text-muted-foreground/30" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-bold uppercase tracking-widest text-primary/70">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="502-468-6882" className="bg-transparent border-x-0 border-t-0 border-b border-primary/20 focus-visible:border-primary focus-visible:ring-0 rounded-none px-0 h-12 text-lg transition-all duration-300 placeholder:text-muted-foreground/30" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="preferred_date" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-bold uppercase tracking-widest text-primary/70">Preferred Date</FormLabel>
              <FormControl>
                <Input placeholder="May 8th, 2026" className="bg-transparent border-x-0 border-t-0 border-b border-primary/20 focus-visible:border-primary focus-visible:ring-0 rounded-none px-0 h-12 text-lg transition-all duration-300 placeholder:text-muted-foreground/30" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="services_interest" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs font-bold uppercase tracking-widest text-primary/70">Treatment Interest</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="bg-transparent border-x-0 border-t-0 border-b border-primary/20 focus-visible:border-primary focus-visible:ring-0 rounded-none px-0 h-12 text-lg shadow-none">
                  <SelectValue placeholder="Select a wellness service" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-background/95 backdrop-blur-xl border-primary/20">
                {SERVICE_OPTIONS.map((option, i) => <SelectItem key={option} value={option} data-index={i}>
                    {option}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Please provide some details about your wellness goals"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs font-bold uppercase tracking-widest text-primary/70">Wellness Goals</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your health and rejuvenation goals..." className="min-h-[100px] bg-transparent border-x-0 border-t-0 border-b border-primary/20 focus-visible:border-primary focus-visible:ring-0 rounded-none px-0 text-lg transition-all duration-300 placeholder:text-muted-foreground/30 resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-lg text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-sm font-bold tracking-[0.2em] uppercase bg-gradient-to-r from-[#B89B5F] to-[#D4AF37] text-white hover:shadow-[0_10px_30px_rgba(184,155,95,0.3)] transition-all duration-500 group rounded-full border-none">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing...
          </> : <>
            Request Session Now
            <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
          </>}
      </Button>
    </Form>;
}
export default function LetsTalkStrategy() {
  return <section data-section-id="234201" id="visit-our-lounge" className="bg-background text-foreground relative overflow-hidden py-24 lg:py-40">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[1000px] h-[1000px] bg-primary/5 rounded-full blur-[160px] opacity-30 translate-x-1/3 -translate-y-1/4" />
        <div className="absolute bottom-0 left-0 w-[800px] h-[800px] bg-chart-2/5 rounded-full blur-[140px] opacity-20 -translate-x-1/4 translate-y-1/4" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.02] mix-blend-overlay" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-start">
          
          {}
          <div className="lg:col-span-5 space-y-16">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/5 border border-primary/10 text-primary text-xs font-bold uppercase tracking-widest">
                <Clock className="size-3" />
                Grand Opening May 8th, 2026
              </div>
              <h2 className="font-serif text-5xl md:text-7xl font-bold tracking-tight leading-[1.1]">
                Visit Our <br />
                <span className="text-primary italic">Lounge</span>
              </h2>
              <p className="text-xl text-muted-foreground font-default leading-relaxed max-w-lg">
                Step into a sanctuary of elite hydration and clinical excellence. Experience the future of wellness in the heart of St. Cloud.
              </p>
            </div>

            <div className="space-y-10">
              {CONTACT_INFO.map((info, i) => <div key={info.label} data-index={i} className="flex items-start gap-6 group">
                  <div className="size-12 rounded-full bg-background border border-primary/20 flex items-center justify-center shrink-0 transition-all duration-500 group-hover:border-primary group-hover:shadow-[0_0_15px_rgba(184,155,95,0.2)]">
                    <info.icon className="size-5 text-primary" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60">{info.label}</p>
                    <p className="font-serif text-xl font-medium leading-snug">{info.value}</p>
                  </div>
                </div>)}
            </div>

            {}
            <div className="relative h-80 w-full rounded-3xl overflow-hidden border border-primary/10 shadow-2xl group">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1661213/img/st_cloud_luxury_map.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1661213/img/st_cloud_luxury_map.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1661213/img/st_cloud_luxury_map.png-wvc-sizes" alt="St Cloud Location Map" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[2000ms] group-hover:scale-110" loading="lazy" />
              <div className="absolute inset-0 bg-background/40 backdrop-blur-[2px] group-hover:backdrop-blur-0 transition-all duration-700" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative">
                  <div className="absolute inset-0 bg-primary/40 rounded-full animate-ping" />
                  <div className="relative size-16 bg-background border border-primary/30 rounded-full flex items-center justify-center shadow-2xl">
                    <MapPin className="size-8 text-primary" />
                  </div>
                </div>
              </div>
              <div className="absolute bottom-6 left-6 right-6 p-4 bg-background/80 backdrop-blur-md border border-primary/10 rounded-2xl translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                <p className="text-xs font-bold uppercase tracking-widest text-primary mb-1">St. Cloud MN</p>
                <p className="text-sm text-muted-foreground">3333 Weet Division Street Suite 145B</p>
              </div>
            </div>

            {}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {SOCIAL_LINKS.map((social, i) => <Link key={social.name} to={social.url} newTab data-index={i} className="block group">
                  <Card className="bg-card/30 backdrop-blur-md border-primary/10 hover:border-primary/30 transition-all duration-500 h-full shadow-sm rounded-2xl">
                    <CardContent className="p-6 flex items-center gap-4">
                      <div className="size-10 rounded-full bg-primary/5 border border-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all duration-500">
                        <social.icon className="size-5" />
                      </div>
                      <div>
                        <p className="font-default font-bold text-sm tracking-wide">{social.name}</p>
                        <p className="text-xs text-muted-foreground">{social.handle}</p>
                      </div>
                    </CardContent>
                  </Card>
                </Link>)}
            </div>
          </div>

          {}
          <div className="lg:col-span-7 relative">
            <div className="absolute -top-12 -right-12 size-64 bg-primary/10 rounded-full blur-3xl opacity-40 animate-pulse" />
            
            <div className="bg-card/40 backdrop-blur-2xl border border-primary/10 rounded-[2.5rem] p-8 md:p-16 shadow-4xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
              
              <div className="mb-12 space-y-4">
                <div className="flex items-center gap-3 text-primary">
                  <SafeHydrate><Calendar className="size-5" /></SafeHydrate>
                  <span className="text-xs font-bold uppercase tracking-[0.3em]">Concierge Booking</span>
                </div>
                <h3 className="font-serif text-4xl font-bold">Begin Your Journey</h3>
                <p className="text-muted-foreground font-default leading-relaxed">
                  Complete the form below and our wellness specialists will contact you within 24 hours to curate your personalized experience.
                </p>
              </div>

              <FormProvider formKey="visit_form_home" formId="visit_form_home" sectionName="visit-our-lounge" defaultValues={{
              name: "",
              email: "",
              phone: "",
              preferred_date: "",
              services_interest: "",
              message: ""
            }} submitText="Request Session Now" successMessage="Your journey to revitalization has begun. Our concierge team will reach out shortly to finalize your appointment.">
                <StrategyForm />
              </FormProvider>
            </div>

            {}
            <div className="mt-12 flex items-center justify-center gap-8 opacity-40 grayscale hover:grayscale-0 transition-all duration-700">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent to-primary/20" />
              <span className="font-serif italic text-primary text-lg">Clinical Excellence. Pure Indulgence.</span>
              <div className="h-px flex-1 bg-gradient-to-l from-transparent to-primary/20" />
            </div>
          </div>

        </div>
      </div>
    </section>;
}